/**
 * Widget Adapter Factory - Refactored Version
 * Creates appropriate widget adapters based on element type and widget availability
 *
 * Key improvements:
 * - Better performance with caching
 * - Improved error handling
 * - Cleaner code structure
 * - Better memory management
 */

import {
    TextInputAdapter,
    NativeSelectAdapter,
    KendoDropDownListAdapter,
    KendoMultiSelectAdapter,
    KendoComboBoxAdapter,
    KendoSwitchAdapter,
    KendoDatePickerAdapter,
    KendoDateRangePickerAdapter
} from './widget-adapter.js';

/**
 * Widget Adapter Factory
 * Creates appropriate adapters for different widget types
 */
export class AdapterFactory {
    // Cache for widget type detection to improve performance
    static _widgetTypeCache = new WeakMap();

    /**
     * Create an adapter for the given element
     * @param {HTMLElement} element The DOM element
     * @returns {WidgetAdapter} Appropriate adapter instance
     */
    static createAdapter(element) {
        if (!element || !element.id) {
            throw new Error('Element must have an id');
        }

        try {
            // Check if this is a Kendo component by looking at parent classes
            const parent = element.parentElement;
            if (!parent) {
                // No parent, treat as text input
                return new TextInputAdapter(element);
            }

            // Use cached widget type if available
            let widgetType = this._widgetTypeCache.get(element);
            if (!widgetType) {
                widgetType = this._detectWidgetType(element, parent);
                this._widgetTypeCache.set(element, widgetType);
            }

            // Create appropriate adapter based on type
            switch (widgetType) {
                case 'kendo-dropdownlist':
                    return this._createKendoAdapter(
                        element,
                        'kendoDropDownList',
                        KendoDropDownListAdapter
                    );
                case 'kendo-multiselect':
                    return this._createKendoAdapter(
                        element,
                        'kendoMultiSelect',
                        KendoMultiSelectAdapter
                    );
                case 'kendo-combobox':
                    return this._createKendoAdapter(element, 'kendoComboBox', KendoComboBoxAdapter);
                case 'kendo-switch':
                    return this._createKendoAdapter(element, 'kendoSwitch', KendoSwitchAdapter);
                case 'kendo-datepicker':
                    return this._createKendoAdapter(
                        element,
                        'kendoDatePicker',
                        KendoDatePickerAdapter
                    );
                case 'kendo-daterangepicker':
                    return this._createKendoAdapter(
                        element,
                        'kendoDateRangePicker',
                        KendoDateRangePickerAdapter
                    );
                case 'native-select':
                    return new NativeSelectAdapter(element);
                case 'kendo-input':
                    // This is a Kendo component input, skip it as it will be handled by the parent
                    throw new Error(
                        `Input ${element.id} belongs to Kendo component, should be handled by parent`
                    );
                case 'text':
                default:
                    return new TextInputAdapter(element);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
                `AdapterFactory: Failed to create adapter for element ${element.id}:`,
                error
            );
            throw error;
        }
    }

    /**
     * Detect widget type for an element
     * @param {HTMLElement} element The DOM element
     * @param {HTMLElement} parent The parent element
     * @returns {string} Widget type identifier
     */
    static _detectWidgetType(element, parent) {
        // Check for Kendo component classes in parent wrapper
        // Kendo 2025: wrapper <span> has classes like k-dropdownlist, k-multiselect, etc.
        if (parent.classList.contains('k-dropdownlist')) return 'kendo-dropdownlist';
        if (parent.classList.contains('k-multiselect')) return 'kendo-multiselect';
        if (parent.classList.contains('k-combobox')) return 'kendo-combobox';
        if (parent.classList.contains('k-switch')) return 'kendo-switch';
        if (parent.classList.contains('k-datepicker')) return 'kendo-datepicker';
        if (parent.classList.contains('k-daterangepicker')) return 'kendo-daterangepicker';

        // Check data-role on the element itself (hidden input has data-role)
        const dataRole = element.getAttribute('data-role');
        if (dataRole) {
            switch (dataRole) {
                case 'dropdownlist': return 'kendo-dropdownlist';
                case 'multiselect': return 'kendo-multiselect';
                case 'combobox': return 'kendo-combobox';
                case 'switch': return 'kendo-switch';
                case 'datepicker': return 'kendo-datepicker';
                case 'daterangepicker': return 'kendo-daterangepicker';
            }
        }

        // Check if this input belongs to a Kendo component by looking for Kendo classes in ancestors
        // Kendo 2025 uses k-picker as base class for date/dropdown components
        const kendoAncestor = element.closest(
            '.k-dropdownlist, .k-switch, .k-multiselect, .k-combobox, .k-datepicker, .k-daterangepicker, .k-picker'
        );
        if (kendoAncestor) {
            return 'kendo-input';
        }

        // For SELECT elements, check if Kendo widget data is attached (MultiSelect, DropDownList, ComboBox)
        // This handles cases where Kendo has initialized but parent wrapper classes aren't detected
        if (element.tagName === 'SELECT') {
            const $el = $(element);
            if ($el.data('kendoMultiSelect')) return 'kendo-multiselect';
            if ($el.data('kendoDropDownList')) return 'kendo-dropdownlist';
            if ($el.data('kendoComboBox')) return 'kendo-combobox';

            // If it's a select element with multiple attribute or specific Kendo attributes, assume it's a multiselect
            if (element.hasAttribute('multiple') || element.hasAttribute('data-kendo-multi-select')) {
                return 'kendo-multiselect';
            }

            // Check if parent has Kendo picker structure (for cases where widget isn't initialized yet)
            const kendoWrapper = element.closest('.k-input, .k-picker');
            if (kendoWrapper) {
                // Determine type based on wrapper structure
                if (kendoWrapper.querySelector('.k-chip-list')) return 'kendo-multiselect';
                return 'kendo-dropdownlist';
            }

            // Plain HTML select without any Kendo widget
            return 'native-select';
        }

        // Default to text input
        return 'text';
    }

    /**
     * Create a Kendo widget adapter with retry mechanism
     * @param {HTMLElement} element The DOM element
     * @param {string} kendoWidgetKey The Kendo widget key (e.g., 'kendoDropDownList')
     * @param {Class} AdapterClass The adapter class to instantiate
     * @returns {WidgetAdapter} Kendo widget adapter
     */
    static _createKendoAdapter(element, kendoWidgetKey, AdapterClass) {
        try {
            // Try to get the Kendo widget immediately
            const widget = $(element).data(kendoWidgetKey);

            if (widget) {
                // Widget is available, create adapter immediately
                return new AdapterClass(widget);
            } else {
                // Widget not available yet, create adapter with null widget
                // The adapter will need to be updated when the widget becomes available
                const adapter = new AdapterClass(null);
                adapter._pendingElement = element;
                adapter._pendingKendoWidgetKey = kendoWidgetKey;
                adapter._pendingAdapterClass = AdapterClass;
                // Store all possible widget keys to try during update (for SELECT elements)
                if (element.tagName === 'SELECT') {
                    adapter._possibleWidgetKeys = ['kendoMultiSelect', 'kendoDropDownList', 'kendoComboBox'];
                }
                return adapter;
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
                `AdapterFactory: Failed to create Kendo adapter for ${element.id}:`,
                error
            );
            throw error;
        }
    }

    /**
     * Create an adapter for a DateRangePicker container element.
     * Unlike regular adapters (created from input/select), this handles the
     * compound DateRangePicker widget which is rendered as a container div.
     * @param {HTMLElement} containerElement The .k-daterangepicker container element
     * @returns {KendoDateRangePickerAdapter} DateRangePicker adapter instance
     */
    static createDateRangePickerAdapter(containerElement) {
        if (!containerElement || !containerElement.id) {
            throw new Error('DateRangePicker container must have an id');
        }

        try {
            const widget = kendo.widgetInstance($(containerElement));

            if (widget) {
                return new KendoDateRangePickerAdapter(widget);
            } else {
                // Widget not yet initialized, create pending adapter
                const adapter = new KendoDateRangePickerAdapter(null);
                adapter._pendingElement = containerElement;
                adapter._pendingKendoWidgetKey = 'kendoDateRangePicker';
                adapter._pendingAdapterClass = KendoDateRangePickerAdapter;
                return adapter;
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
                `AdapterFactory: Failed to create DateRangePicker adapter for ${containerElement.id}:`,
                error
            );
            throw error;
        }
    }

    /**
     * Update a pending adapter when the Kendo widget becomes available
     * @param {WidgetAdapter} adapter The adapter to update
     * @returns {boolean} True if adapter was updated successfully
     */
    static updatePendingAdapter(adapter) {
        if (!adapter._pendingElement || !adapter._pendingKendoWidgetKey) {
            return false;
        }

        try {
            const $el = $(adapter._pendingElement);
            let widget = null;

            // Try the primary widget key first
            widget = $el.data(adapter._pendingKendoWidgetKey);

            // For container elements (e.g., DateRangePicker div), try kendo.widgetInstance
            if (!widget && adapter._pendingElement.tagName !== 'INPUT' && adapter._pendingElement.tagName !== 'SELECT') {
                widget = kendo.widgetInstance($el);
            }

            // If not found and we have alternative keys to try (for SELECT elements)
            if (!widget && adapter._possibleWidgetKeys) {
                for (const widgetKey of adapter._possibleWidgetKeys) {
                    widget = $el.data(widgetKey);
                    if (widget) {
                        // Update the adapter class based on actual widget type
                        adapter._pendingKendoWidgetKey = widgetKey;
                        break;
                    }
                }
            }

            if (widget) {
                // Widget is now available, update the adapter
                adapter.widget = widget;

                // Clean up pending state
                delete adapter._pendingElement;
                delete adapter._pendingKendoWidgetKey;
                delete adapter._pendingAdapterClass;
                delete adapter._possibleWidgetKeys;

                // Apply any pending event bindings
                if (adapter.applyPendingBindings) {
                    adapter.applyPendingBindings();
                }

                return true;
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('AdapterFactory: Failed to update pending adapter:', error);
        }

        return false;
    }

    /**
     * Check if an adapter is pending (waiting for Kendo widget)
     * @param {WidgetAdapter} adapter The adapter to check
     * @returns {boolean} True if adapter is pending
     */
    static isAdapterPending(adapter) {
        return !!(adapter._pendingElement && adapter._pendingKendoWidgetKey);
    }

    /**
     * Get the element that a pending adapter is waiting for
     * @param {WidgetAdapter} adapter The adapter to check
     * @returns {HTMLElement|null} The element or null if not pending
     */
    static getPendingElement(adapter) {
        return adapter._pendingElement || null;
    }
}

/**
 * Widget type detection utilities - Refactored Version
 *
 * Key improvements:
 * - Better performance with caching
 * - Cleaner code structure
 * - Improved error handling
 */
export class WidgetTypeDetector {
    // Cache for widget type detection to improve performance
    static _typeCache = new WeakMap();

    /**
     * Detect the widget type of an element
     * @param {HTMLElement} element The DOM element
     * @returns {string} Widget type identifier
     */
    static detectWidgetType(element) {
        if (!element) {
            return 'text';
        }

        // Check cache first
        let type = this._typeCache.get(element);
        if (type) {
            return type;
        }

        try {
            type = this._detectWidgetTypeInternal(element);
            this._typeCache.set(element, type);
            return type;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('WidgetTypeDetector: Failed to detect widget type:', error);
            return 'text';
        }
    }

    /**
     * Internal method to detect widget type
     * @param {HTMLElement} element The DOM element
     * @returns {string} Widget type identifier
     */
    static _detectWidgetTypeInternal(element) {
        if (!element.parentElement) {
            return 'text';
        }

        const parent = element.parentElement;

        // Check parent classes first (most common case)
        // Kendo 2025: wrapper <span> has classes like k-dropdownlist, k-multiselect, etc.
        if (parent.classList.contains('k-dropdownlist')) return 'kendo-dropdownlist';
        if (parent.classList.contains('k-multiselect')) return 'kendo-multiselect';
        if (parent.classList.contains('k-combobox')) return 'kendo-combobox';
        if (parent.classList.contains('k-switch')) return 'kendo-switch';
        if (parent.classList.contains('k-datepicker')) return 'kendo-datepicker';
        if (parent.classList.contains('k-daterangepicker')) return 'kendo-daterangepicker';

        // Check data-role on the element itself (hidden input has data-role)
        const dataRole = element.getAttribute('data-role');
        if (dataRole) {
            switch (dataRole) {
                case 'dropdownlist': return 'kendo-dropdownlist';
                case 'multiselect': return 'kendo-multiselect';
                case 'combobox': return 'kendo-combobox';
                case 'switch': return 'kendo-switch';
                case 'datepicker': return 'kendo-datepicker';
                case 'daterangepicker': return 'kendo-daterangepicker';
            }
        }

        // Check if this input belongs to a Kendo component
        // Kendo 2025 uses k-picker as base class for date/dropdown components
        const kendoAncestor = element.closest(
            '.k-dropdownlist, .k-switch, .k-multiselect, .k-combobox, .k-datepicker, .k-daterangepicker, .k-picker'
        );
        if (kendoAncestor) {
            return 'kendo-input'; // Special type for inputs that belong to Kendo components
        }

        // Plain HTML <select> without Kendo widget
        if (element.tagName === 'SELECT') {
            return 'native-select';
        }

        return 'text';
    }

    /**
     * Check if an element is a Kendo component
     * @param {HTMLElement} element The DOM element
     * @returns {boolean} True if element is a Kendo component
     */
    static isKendoComponent(element) {
        const type = this.detectWidgetType(element);
        return type.startsWith('kendo-');
    }

    /**
     * Check if an element is a text input
     * @param {HTMLElement} element The DOM element
     * @returns {boolean} True if element is a text input
     */
    static isTextInput(element) {
        return this.detectWidgetType(element) === 'text';
    }

    /**
     * Clear the type cache (useful for testing or memory management)
     */
    static clearCache() {
        this._typeCache = new WeakMap();
    }
}
